import Column from '@app/components/layout/Column'
import { Title } from '@app/components/core/typography/Title'
import { Style, styled } from 'inlines'
import { ReactNode } from 'react'
import { useInfiniteScroll } from '@app/util/useInfiniteScroll'
import useMediaQuery from '@app/util/useMediaQuery'
import Row from '@app/components/layout/Row'
import { Tag } from '@app/components/core/tag/Tag'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { Text } from '@app/components/core/typography/Text'

export type ActiveFilterChip = {
  key: string
  label: string
  onRemove: () => void
}

export type SearchGridProps = {
  items: ReactNode[]
  hasMore: boolean
  title: string
  subtitle: string
  searchTerm?: string
  isLoading: boolean
  gridStyle: Style
  loadMore: () => void
  onResetSearch: () => void
  footer?: ReactNode
  activeFilters?: ActiveFilterChip[]
}

export const SearchGrid = ({
  items,
  hasMore,
  title,
  subtitle,
  searchTerm,
  isLoading,
  gridStyle,
  loadMore,
  onResetSearch,
  footer,
  activeFilters,
}: SearchGridProps) => {
  const isMobile = useMediaQuery()
  const { lastItemRef } = useInfiniteScroll({
    isLoading,
    hasMore,
    loadMore,
    itemCount: items.length,
  })

  const appliedGridStyle: Style = isMobile
    ? {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }
    : gridStyle

  return (
    <Column alignItems="stretch" style={{ flexGrow: 0 }} gap={'2rem'}>
      <div>
        <Title.H3 style={{ alignSelf: 'start' }}>{title}</Title.H3>
        {searchTerm || (activeFilters && activeFilters.length > 0) ? (
          <Row style={{ flexWrap: 'wrap', gap: '1rem' }}>
            {searchTerm && (
              <Tag
                variant="subtle"
                size="small"
                label={searchTerm}
                iconRight={faXmark}
                onClick={onResetSearch}
              />
            )}
            {activeFilters?.map((chip) => (
              <Tag
                key={chip.key}
                variant="subtle"
                size="small"
                label={chip.label}
                iconRight={faXmark}
                onClick={chip.onRemove}
              />
            ))}
          </Row>
        ) : (
          <Text.Medium>{subtitle}</Text.Medium>
        )}
      </div>

      <styled.div style={appliedGridStyle}>
        {items &&
          items.map((item, index) => {
            if (items.length === index + 1) {
              return (
                <styled.div key={index} ref={lastItemRef}>
                  {item}
                </styled.div>
              )
            }
            return <styled.div key={index}>{item}</styled.div>
          })}
      </styled.div>
      {footer}
    </Column>
  )
}
